import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  BillingCycle,
  GymAdminSummary,
  PaymentMethod,
  SubscriptionPlan,
  SubscriptionStatus,
} from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Modal, Input, Select, Textarea, Skeleton, EmptyState, cn } from '@techsofit/ui';
import {
  getGyms,
  getRevenue,
  getSubscriptionPlans,
  getGymPayments,
  recordGymPayment,
  changeGymPlan,
  cancelGymSubscription,
  reactivateGymSubscription,
  grantGymTrial,
} from '../lib/adminApi';
import { useAsync } from '../lib/useAsync';
import { money } from '../lib/format';

const STATUS_STYLE: Record<SubscriptionStatus, string> = {
  ACTIVE: 'bg-ok-soft text-ok-ink',
  TRIAL: 'bg-neutral-soft text-neutral-ink',
  PAST_DUE: 'bg-bad-soft text-bad-ink',
  CANCELED: 'bg-neutral-soft text-neutral-ink',
};

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  ACTIVE: 'Active',
  TRIAL: 'Free',
  PAST_DUE: 'Past due',
  CANCELED: 'Canceled',
};

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'ECOCASH', label: 'EcoCash' },
  { value: 'ONEMONEY', label: 'OneMoney' },
  { value: 'INNBUCKS', label: 'InnBucks' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'OTHER', label: 'Other' },
];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function BillingPage() {
  const navigate = useNavigate();
  const { data: gyms, loading, error } = useAsync(getGyms);
  const { data: revenue } = useAsync(getRevenue);

  return (
    <section className="mt-6 animate-fade-up">
      <div className="mb-4">
        <h1 className="font-display text-display-md text-ink">Gyms &amp; billing</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Record what each gym pays for its subscription — that activates them, sets their renewal date, and
          lifts the free 3-member cap. Status is worked out from payments, just like member memberships.
        </p>
      </div>

      <RevenuePanel
        mrr={revenue?.mrr}
        thisMonth={revenue?.collectedThisMonth}
        allTime={revenue?.collectedAllTime}
        active={revenue?.activeGyms}
        pastDue={revenue?.pastDueGyms}
      />

      <Card className="mt-4 overflow-hidden p-0">
        <div className="hidden grid-cols-[1.6fr_1fr_0.8fr_0.8fr_auto] gap-4 border-b border-line px-5 py-3 text-eyebrow uppercase text-muted md:grid">
          <span>Gym</span><span>Plan</span><span>Status</span><span>Members</span><span className="text-right">Renews</span>
        </div>
        {loading ? (
          <div className="flex flex-col gap-2 p-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : error ? (
          <EmptyState title="Couldn’t load gyms" description={error} className="rounded-none shadow-none" />
        ) : (gyms ?? []).length ? (
          <ul className="divide-y divide-line">
            {gyms!.map((g) => (
              <li
                key={g.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/gyms/${g.id}`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/gyms/${g.id}`)}
                className="grid cursor-pointer grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 transition-colors hover:bg-surface md:grid-cols-[1.6fr_1fr_0.8fr_0.8fr_auto] md:px-5"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">{g.name}</p>
                  <p className="truncate text-sm text-muted">{g.slug}</p>
                </div>
                <span className="hidden text-sm text-ink-soft md:block">
                  {g.planName}{g.paidPlan ? ` · ${g.billingCycle.toLowerCase()}` : ''}
                </span>
                <span className="md:justify-self-start">
                  <span className={cn('inline-flex rounded-pill px-2.5 py-1 text-xs font-medium', STATUS_STYLE[g.subscriptionStatus])}>
                    {STATUS_LABEL[g.subscriptionStatus]}
                  </span>
                </span>
                <span className="hidden text-sm tabular text-ink-soft md:block">{g.memberCount}</span>
                <span className="justify-self-end text-sm tabular text-muted">{g.paidPlan ? g.currentPeriodEnd ?? '—' : '—'}</span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="No gyms yet" description="Tenants appear here once they sign up." className="rounded-none shadow-none" />
        )}
      </Card>

    </section>
  );
}

function RevenuePanel({
  mrr,
  thisMonth,
  allTime,
  active,
  pastDue,
}: {
  mrr?: string;
  thisMonth?: string;
  allTime?: string;
  active?: number;
  pastDue?: number;
}) {
  const stats = [
    { label: 'Recurring / mo (MRR)', value: mrr == null ? '—' : money(mrr) },
    { label: 'Collected this month', value: thisMonth == null ? '—' : money(thisMonth) },
    { label: 'Collected all-time', value: allTime == null ? '—' : money(allTime) },
    { label: 'Paying gyms', value: active == null ? '—' : `${active}${pastDue ? ` · ${pastDue} past due` : ''}` },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {stats.map((s) => (
        <Card key={s.label} className="p-4">
          <p className="text-xs font-medium text-muted">{s.label}</p>
          <p className="mt-1 font-display text-2xl font-semibold tabular text-ink">{s.value}</p>
        </Card>
      ))}
    </div>
  );
}

export function ManageModal({ gym, onClose, onSaved }: { gym: GymAdminSummary; onClose: () => void; onSaved: () => void }) {
  const { data: plans } = useAsync(getSubscriptionPlans);
  const { data: history } = useAsync(() => getGymPayments(gym.id), [gym.id]);

  const [planId, setPlanId] = useState('');
  const [cycle, setCycle] = useState<BillingCycle>(gym.billingCycle);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [paidOn, setPaidOn] = useState(today());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preselect the gym's current plan once the catalog loads.
  useEffect(() => {
    if (!plans || planId) return;
    const current = plans.find((p) => p.name === gym.planName);
    if (current) setPlanId(current.id);
  }, [plans, gym.planName, planId]);

  const plan: SubscriptionPlan | undefined = plans?.find((p) => p.id === planId);
  const isPaidPlan = plan != null && Number(plan.monthlyPrice) > 0;
  const suggested = plan ? (cycle === 'ANNUAL' ? plan.annualPrice : plan.monthlyPrice) : '';

  // Keep the amount in step with the chosen plan + cycle (until the admin edits it).
  useEffect(() => {
    if (isPaidPlan) setAmount(String(suggested));
    else setAmount('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, cycle]);

  async function recordPayment() {
    if (!plan) return;
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError('Enter the amount paid.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await recordGymPayment(gym.id, {
        planId: plan.id,
        amount: value,
        method,
        billingCycle: cycle,
        paidOn,
        note: note.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not record the payment.');
      setSaving(false);
    }
  }

  async function moveToFree() {
    if (!plan) return;
    setError(null);
    setSaving(true);
    try {
      await changeGymPlan(gym.id, { planId: plan.id });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not change the plan.');
      setSaving(false);
    }
  }

  async function grantTrial(days: number) {
    setError(null);
    setSaving(true);
    try {
      await grantGymTrial(gym.id, days);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not grant the trial.');
      setSaving(false);
    }
  }

  async function toggleCancel() {
    setError(null);
    setSaving(true);
    try {
      if (gym.subscriptionStatus === 'CANCELED') await reactivateGymSubscription(gym.id);
      else await cancelGymSubscription(gym.id);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not update the subscription.');
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      className="sm:max-w-xl"
      title={gym.name}
      description={
        <>
          Currently <strong>{gym.planName}</strong> ·{' '}
          <span className={cn('rounded-pill px-1.5 py-0.5 text-xs font-medium', STATUS_STYLE[gym.subscriptionStatus])}>
            {STATUS_LABEL[gym.subscriptionStatus]}
          </span>
          {gym.paidPlan && gym.currentPeriodEnd ? ` · renews ${gym.currentPeriodEnd}` : ''}
        </>
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={toggleCancel}
            disabled={saving}
            className={cn('text-sm font-medium underline-offset-2 hover:underline disabled:opacity-50',
              gym.subscriptionStatus === 'CANCELED' ? 'text-ink' : 'text-bad-ink')}
          >
            {gym.subscriptionStatus === 'CANCELED' ? 'Reactivate' : 'Cancel subscription'}
          </button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Close</Button>
            {isPaidPlan ? (
              <Button onClick={recordPayment} loading={saving}>Record payment</Button>
            ) : (
              <Button onClick={moveToFree} loading={saving} disabled={!plan || plan.name === gym.planName}>
                Move to {plan?.name ?? 'Free'}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Select label="Plan" value={planId} onChange={(e) => setPlanId(e.target.value)}>
          {/* Only offer live plans; keep the gym's current plan visible even if it's been retired. */}
          {(plans ?? []).filter((p) => p.active || p.id === planId).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {Number(p.monthlyPrice) > 0 ? `${money(p.monthlyPrice)}/mo` : 'Free'} ·{' '}
              {p.memberCap == null ? 'unlimited' : `${p.memberCap} members`}
            </option>
          ))}
        </Select>

        {isPaidPlan ? (
          <>
            <p className="-mb-1 text-xs font-medium text-muted">Record the payment they made for this plan</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Select label="Billing cycle" value={cycle} onChange={(e) => setCycle(e.target.value as BillingCycle)}>
                <option value="MONTHLY">Monthly</option>
                <option value="ANNUAL">Annual</option>
              </Select>
              <Input label="Amount paid (USD)" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
              <Select label="Method" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </Select>
              <Input label="Paid on" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            </div>
            <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reference, who took the payment, etc." />
            <p className="text-xs text-muted">
              Recording this sets them to <strong>Active</strong> and extends their renewal by one {cycle === 'ANNUAL' ? 'year' : 'month'}.
            </p>
          </>
        ) : (
          <p className="rounded-control bg-surface px-3.5 py-2.5 text-sm text-ink-soft">
            <strong>{plan?.name ?? 'Free'}</strong> is a free tier — no payment needed. Moving a gym here caps it at
            3 members. To upgrade, pick a paid plan above.
          </p>
        )}

        <div className="rounded-control border border-line bg-surface p-3">
          <p className="text-sm font-medium text-ink">Give a free trial</p>
          <p className="mt-0.5 text-xs text-muted">
            Comp/deal — full features, no payment. Extends a current trial or grants one to a paying gym.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {[1, 2, 3, 6, 12].map((mo) => (
              <button
                key={mo}
                type="button"
                disabled={saving}
                onClick={() => grantTrial(mo * 30)}
                className="rounded-pill border border-line px-3 py-1 text-sm text-ink hover:bg-elevated disabled:opacity-50"
              >
                {mo} {mo === 1 ? 'month' : 'months'}
              </button>
            ))}
          </div>
        </div>

        <PaymentHistory rows={history} />

        {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
      </div>
    </Modal>
  );
}

function PaymentHistory({ rows }: { rows: import('@techsofit/api-types').GymPayment[] | null }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="border-t border-line pt-3">
        <p className="text-xs font-medium text-muted">No payments recorded yet.</p>
      </div>
    );
  }
  const methodLabel = (m: PaymentMethod) => METHODS.find((x) => x.value === m)?.label ?? m;
  return (
    <div className="border-t border-line pt-3">
      <p className="mb-2 text-xs font-medium uppercase text-muted">Payment history</p>
      <ul className="flex flex-col gap-1.5">
        {rows.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-ink-soft">
              {p.paidOn} · {p.planName} · {methodLabel(p.method)}
            </span>
            <span className="tabular font-medium text-ink">{money(p.amount)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
